import { useState } from 'react';
import { useSearchParamsWithApiKey, useDebouncedEffect } from '../../tools';
import { FIX_LOGO_CONTRAST_PARAM, SOURCE_URL_PARAM, TARGET_URL_PARAM } from '../../constants';

interface TransferStyleParams {
    sourceUrl: string;
    targetUrl: string;
    fixLogoContrast: boolean;
    enableMulticolorLogoContrastFix: boolean;
    setSourceUrl: (sourceUrl: string) => void;
    setTargetUrl: (targetUrl: string) => void;
    setFixLogoContrast: (fixLogoContrast: boolean) => void;
    setEnableMulticolorLogoContrastFix: (enableMulticolorLogoContrastFix: boolean) => void;
}

export const useTransferStyleParams = (): TransferStyleParams => {
    const [searchParams, setSearchParams] = useSearchParamsWithApiKey();
    const sourceUrlParam = searchParams.get(SOURCE_URL_PARAM);
    const targetUrlParam = searchParams.get(TARGET_URL_PARAM);

    const fixLogoContrastParam = searchParams.has('fixLogoContrast')
        ? searchParams.get('fixLogoContrast') === 'true'
        : false;

    const enableMulticolorLogoContrastFixParam = searchParams.has('enableMulticolorLogoContrastFix')
        ? searchParams.get('enableMulticolorLogoContrastFix') === 'true'
        : false;

    const [sourceUrl, setSourceUrl] = useState(sourceUrlParam ?? '');
    const [targetUrl, setTargetUrl] = useState(targetUrlParam ?? '');
    const [fixLogoContrast, setFixLogoContrast] = useState(!!fixLogoContrastParam || false);
    const [enableMulticolorLogoContrastFix, setEnableMulticolorLogoContrastFix] = useState(
        !!enableMulticolorLogoContrastFixParam || false,
    );

    useDebouncedEffect(
        () => {
            const searchParamsObj: Record<string, string> = {};

            if (sourceUrl) {
                searchParamsObj[SOURCE_URL_PARAM] = sourceUrl;
            }

            if (targetUrl) {
                searchParamsObj[TARGET_URL_PARAM] = targetUrl;
            }

            if (fixLogoContrast) {
                searchParamsObj[FIX_LOGO_CONTRAST_PARAM] = fixLogoContrast.toString();
            }

            if (enableMulticolorLogoContrastFix) {
                searchParamsObj['enableMulticolorLogoContrastFix'] = enableMulticolorLogoContrastFix.toString();
            }

            setSearchParams(searchParamsObj, { replace: true });
        },
        [sourceUrl, targetUrl, fixLogoContrast, enableMulticolorLogoContrastFix, setSearchParams],
        500,
    );

    return {
        sourceUrl,
        targetUrl,
        fixLogoContrast,
        enableMulticolorLogoContrastFix,
        setSourceUrl,
        setTargetUrl,
        setFixLogoContrast,
        setEnableMulticolorLogoContrastFix,
    };
};
